import {IncludeFragment} from '@github-ui/include-fragment-react'
import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import {SecuritySeverityBadge} from '@github-ui/code-scanning-shared/components/SecuritySeverityBadge'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {ActivityHeaderHeadingProps} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ActivityHeader, CommentAuthorTypes} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CommentActions} from '@github-ui/commenting/CommentActions'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {VALUES} from '@github-ui/commenting/Values'
import {noop} from '@github-ui/noop'
import type {ReactionContent, ReactionViewerGroup} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'
import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents, type GitHubUrlReference} from '@github-ui/copilot-chat-types'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {AlertIcon, CopilotIcon, StopIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, Label, LabelGroup, Spinner} from '@primer/react'
import {useCallback, useMemo, useRef, useState} from 'react'
import {useCommentAnchor} from '../../hooks/use-comment-anchor'
import type {Comment} from '../../conversation-types'
import {AutomatedCommentSource, SuggestionStates} from '../../conversation-types'
import styles from './AutomatedReviewThreadComment.module.css'
import {AutomatedReviewSuggestion} from './AutomatedReview/AutomatedReviewSuggestion'
import type {AssignToCopilotProps} from './AutomatedReview/AssignToCopilotButton'
import {Blankslate, Dialog} from '@primer/react/experimental'
import {AutomatedReviewFeedback} from './AutomatedReview/AutomatedReviewFeedback'
import {CopilotApplyChangesButton} from '@github-ui/copilot-apply-changes-button/CopilotApplyChangesButton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useReactToCommentMutationWithCallback} from '@github-ui/pull-requests/use-react-to-comment-mutation'
import {buildCommentSnapshot} from '@github-ui/pull-requests/helpers/comment-snapshot'
import {CommentSnapshotProvider} from './AutomatedReview/CommentSnapshotContext'
import {useActiveRoute} from '@github-ui/pull-requests/hooks/use-active-route'
import {useIsUnifiedBatchEnabled} from '@github-ui/pull-requests/hooks/use-unified-batch-enabled'

type CommentState = 'editing' | 'hidden' | 'visible'

export interface AutomatedReviewThreadCommentProps {
  anchorPrefix?: string
  basePath: string
  comment: Comment
  /**
   * If true, the comment will scroll itself into view and show a blue border
   * when the component renders with a hash that matches the comment.
   */
  isAnchorable?: boolean
  isAgenticAutofixEnabled?: boolean
  /**
   * Whether Fix with Copilot actions are available in the React Files Changed experience.
   * Classic rendering omits this and remains governed by server-authorized props.
   */
  isFixWithCopilotAvailable?: boolean
  isInDialogMode?: boolean | undefined
  isOutdated?: boolean
  isThreadResolved?: boolean
  /**
   * Optional URL for fetching available models for the "Fix with Copilot" model picker.
   */
  modelsUrl?: string
  assignToCopilotProps?: AssignToCopilotProps
  onQuoteReply?: (quotedText?: string) => void
  originalDiffPathUri?: string | null
  /**
   * If true, the "Apply changes" button will be shown, allowing Copilot to
   * automatically apply suggested changes from the code review comment.
   * This is determined server-side based on feature flags, user permissions,
   * and whether the comment contains a suggestion.
   */
  showApplyChangesButton?: boolean
  threadId: string
  threadPositionNumber?: number
  footnoteTextHTML?: string
  /**
   * If true, uses classic experience labels (e.g., "Commit suggestion" instead of "Apply suggestion")
   * and shows the batch suggestions button as inactive.
   */
  isClassicExperience?: boolean
}

function formatBotAuthorLogin(login?: string): string | undefined {
  if (!login) {
    return undefined
  }

  // Change the format of bots (eg.: `github-code-quality[bot]` to `github-code-quality`)
  return login.replace(/\[bot\]$/i, '')
}

export function AutomatedReviewThreadComment({
  anchorPrefix = 'r',
  basePath,
  comment,
  isAnchorable = false,
  isAgenticAutofixEnabled = false,
  isFixWithCopilotAvailable,
  isClassicExperience,
  isInDialogMode,
  isOutdated,
  isThreadResolved = false,
  modelsUrl,
  assignToCopilotProps,
  onQuoteReply = noop,
  originalDiffPathUri,
  showApplyChangesButton = false,
  threadId,
  threadPositionNumber,
  footnoteTextHTML,
}: AutomatedReviewThreadCommentProps): React.ReactElement {
  const {addToast} = useToastContext()
  const [isMinimized, setIsMinimized] = useState(comment.isHidden)
  const [visibilityErrorMessage] = useState('')
  const [reactionGroups, setReactionGroups] = useState<ReactionViewerGroup[] | undefined>(comment.reactionGroups)
  const [isCodePathsDialogOpen, setIsCodePathsDialogOpen] = useState(false)
  const surface = useActiveRoute()

  const commentBodyRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reactionButtonRef = useRef<HTMLButtonElement>(null)
  const {
    id: automatedCommentId,
    title,
    message,
    codePathsUrl,
    severity,
    securitySeverity,
    source,
    aiAuthored,
    isDismissed,
    isFixed,
    suggestion,
    suggestionState,
    suggestionErrorMessage,
    feedbackOptions,
    showFeedbackButtons: showFeedbackButtonsInput,
    showAiNote,
    showFixWithCopilotButton,
    showAiLabel,
    foldLongSuggestion,
    hideSeverityLabel,
    commitMessageGenerationConfig,
  } = comment.automatedComment || {}

  // this is needed because any properties coming from comment.automatedComment can be null
  // and cannot be used directly in AutomatedReviewFeedback
  const showFeedbackButtons =
    showFeedbackButtonsInput && automatedCommentId && feedbackOptions && !isAgenticAutofixEnabled

  const unifiedBatchPrCommentsEnabled = useIsUnifiedBatchEnabled() && !isClassicExperience
  const askCopilotEntryPointEnabled = isFeatureEnabled('copilot_chat_automated_review_entrypoint')
  const showReactions = (reactionGroups?.length ?? 0) > 0 && !isAgenticAutofixEnabled
  const showFootnoteText = (footnoteTextHTML || showAiNote) && !isAgenticAutofixEnabled
  const showFootBar = showFeedbackButtons || showReactions || showFootnoteText
  const showLegacyApplyChangesButton =
    showApplyChangesButton && !unifiedBatchPrCommentsEnabled && !isAgenticAutofixEnabled
  const visibleAssignToCopilotProps = !isFixed && !isDismissed && !isThreadResolved ? assignToCopilotProps : undefined

  const onReactSuccessCallback = (data: {reactionGroups: ReactionViewerGroup[]}) => {
    setReactionGroups(data.reactionGroups)
  }
  const {mutate: reactToCommentMutation} = useReactToCommentMutationWithCallback(basePath, onReactSuccessCallback)

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) => {
    if (!comment.databaseId) throw new Error('Comment not found')

    setTimeout(() => reactionButtonRef.current?.focus(), 0)
    reactToCommentMutation(
      {
        commentDatabaseId: comment.databaseId,
        threadId,
        reaction,
        viewerHasReacted,
      },
      {
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: 'Failed to react to comment',
          })
        },
      },
    )
  }

  // TODO(@aliceclv): figure out when the `.id` value gets populated to understand if it's a safe fallback
  // The unique identifier for this comment in the DOM
  const commentId = comment.databaseId?.toString() || comment.id
  // Snapshot used to populate a CCR batch item when the suggestion is added to
  // the unified batch. Only built when the feature is enabled — the consumer
  // (`SuggestionBatchActions`) never renders otherwise, so building it on every
  // automated comment on the flag-off path would be wasted work.
  const commentSnapshot = useMemo(
    () => (unifiedBatchPrCommentsEnabled ? buildCommentSnapshot(comment) : undefined),
    [comment, unifiedBatchPrCommentsEnabled],
  )
  // The anchor portion of the URL that will target this specific comment when navigating to it
  // This is used to scroll the comment into view and highlight it when directly linked
  const commentAnchor = commentId ? `${anchorPrefix}${commentId}` : undefined
  // Note(@aliceclv): Comment is not editable
  const commentState: CommentState = isMinimized ? 'hidden' : 'visible'

  // The full URL including anchor that points directly to this comment within the current page
  const pageScopedCommentHref =
    commentAnchor && ssrSafeLocation
      ? new URL(`${ssrSafeLocation.pathname}#${commentAnchor}`, ssrSafeLocation.origin).toString()
      : comment.url || ''

  const ASK_COPILOT_TELEMETRY_SOURCE = 'automated-review-ask-copilot'
  const askCopilotButtonId = `${ASK_COPILOT_TELEMETRY_SOURCE}-${commentId}`
  const handleAskCopilot = useCallback(() => {
    const references: GitHubUrlReference[] = [
      {
        type: 'github-url',
        url: comment.url,
        title: title ?? 'Copilot code review comment',
      },
    ]

    publishOpenCopilotChat({
      intent: CopilotChatIntents.conversation,
      newThread: true,
      references,
      id: askCopilotButtonId,
      telemetrySource: `element ${ASK_COPILOT_TELEMETRY_SOURCE}`,
    })
  }, [askCopilotButtonId, comment.url, title])

  const askCopilotAction = askCopilotEntryPointEnabled ? (
    <Button
      aria-label="Ask Copilot"
      id={askCopilotButtonId}
      size={isClassicExperience ? 'small' : undefined}
      leadingVisual={CopilotIcon}
      onClick={handleAskCopilot}
    >
      Ask
    </Button>
  ) : undefined

  // Mirrors when `AutomatedReviewSuggestion` renders an action row, so the legacy row never repeats the button.
  const suggestionHostsAskCopilot =
    Boolean(automatedCommentId) && Boolean(suggestion) && suggestionState === SuggestionStates.Present

  useCommentAnchor({
    isAnchorable,
    commentId,
    commentAnchor,
    containerRef,
  })

  const commentActivityProps = {
    headingProps: {as: 'h3'} as ActivityHeaderHeadingProps,
    avatarUrl: comment.author?.avatarUrl ?? VALUES.ghostUser.avatarUrl,
    comment: {
      ...comment,
      bodyForClipboard: comment.body,
      url: pageScopedCommentHref,
      referenceText: comment.reference.text ?? `#${comment.reference.number}`,
      // Note(@aliceclv): none of these actions are allowed in the context of an automated comment
      viewerCanMinimize: false,
      viewerCanUpdate: false,
      viewerCanDelete: false,
      viewerCanBlockFromOrg: false,
      viewerCanUnblockFromOrg: false,
      viewerCanReport: false,
      viewerCanReportToMaintainer: false,
      viewerCanReferenceInIssue: false,
      viewerCanQuoteReply: false,
    },
    commentRef: containerRef,
    editHistoryComponent: undefined,
    onBlock: undefined,
    onUnblock: undefined,
    onMinimize: setIsMinimized,
    onSuccessfulBlock: () => {
      //not ideal, but this is the easiest way to get all of the comments to reflect the newly updated state
      setTimeout(() => ssrSafeWindow?.location.reload(), 800)
    },
    isMinimized,
    navigate: noop,
    onReplySelect: onQuoteReply,
    isReply: false,
    forceInlineAvatar: true,
    noContainerStyles: true,
    originalDiffPathUri,
    isOutdated,
    // Comment actions are null as they are not allowed for automated comments
    editComment: () => null,
    deleteComment: () => null,
    hideComment: () => null,
    unhideComment: () => null,

    // For CCR (Copilot Code Review) ARCs, use the same Copilot identity as the
    // old non-ARC path so ActivityHeader renders the "AI" chip instead of "bot".
    commentAuthorSlug: aiAuthored ? 'copilot-pull-request-reviewer' : undefined,
    commentAuthorType: CommentAuthorTypes.Bot,
    commentAuthorLogin: aiAuthored
      ? 'Copilot'
      : (formatBotAuthorLogin(comment.author?.login) ?? VALUES.ghostUser.login),
    hideProfileLink: true,
  }

  const getCommentAriaLabel = (): string => {
    if (threadPositionNumber !== undefined) {
      return `Comment ${threadPositionNumber}`
    } else {
      return 'Comment'
    }
  }

  const aiLabel = showAiLabel ? (
    <Label variant="secondary" aria-label="Generated by AI" className="ml-1">
      AI
    </Label>
  ) : undefined
  const securitySeverityBadge =
    !hideSeverityLabel && securitySeverity ? <SecuritySeverityBadge severity={securitySeverity} /> : null
  // Copilot comments use the same severity levels but with different labels and colors, so we need to handle them separately
  // TODO: After ExP validation we should turn this into a dedicated component or extend RuleSeverityBadge to handle
  // Copilot severities and remove the source check
  const isCopilotSource = source === AutomatedCommentSource.Copilot
  const copilotSeverityVariant = severity === 'high' ? 'danger' : severity === 'medium' ? 'attention' : 'accent'
  const ruleSeverityBadge =
    !hideSeverityLabel && severity ? (
      isCopilotSource ? (
        <Label variant={copilotSeverityVariant}>{severity.charAt(0).toUpperCase() + severity.slice(1)}</Label>
      ) : (
        <RuleSeverityBadge severity={severity} />
      )
    ) : null
  const hasBadges = securitySeverityBadge || ruleSeverityBadge

  return (
    <div
      ref={containerRef}
      id={isAnchorable ? commentAnchor : undefined}
      className={styles.automatedComment}
      style={{scrollMarginTop: isAnchorable ? '120px' : 0}}
      data-first-thread-comment
      data-marker-navigation-comment-thread-id={threadId}
      data-marker-navigation-comment-id={comment.id}
      data-testid="automated-review-comment"
      // When aria-label and role is set while still in gridcell mode, the browser doesn't the respect the
      // aria-hidden"true" and we end up with the aria-labels leaking into the accessible name of the gridcell,
      // which is not what we want. So we should only set these attributes while in dialog mode.
      {...(isInDialogMode
        ? {
            role: 'document',
            'aria-roledescription': 'comment',
            'aria-label': getCommentAriaLabel(),
          }
        : {})}
    >
      <div className={styles.automatedComment__headerContainer}>
        <ActivityHeader
          {...commentActivityProps}
          commentBody={comment.body}
          isInDialogMode={isInDialogMode}
          actions={<CommentActions {...commentActivityProps} />}
          afterDateContent={aiLabel}
          badges={
            hasBadges ? (
              <LabelGroup>
                {securitySeverityBadge}
                {ruleSeverityBadge}
              </LabelGroup>
            ) : undefined
          }
          isOutdated={isOutdated}
        />
      </div>
      {visibilityErrorMessage && (
        <Flash variant="danger" className="tmp-m-3">
          <StopIcon className="mr-2" />
          {visibilityErrorMessage}
        </Flash>
      )}
      <div className={styles.automatedComment__bodyContainer}>
        {commentState === 'visible' && (
          <div className={styles.automatedComment__body}>
            <div className={styles.automatedComment__content}>
              {title && (
                <div className="d-flex flex-items-center">
                  <h3 className={styles.automatedComment__title}>{title}</h3>
                </div>
              )}
              <VerifiedHTMLText
                ref={commentBodyRef}
                className={`${styles.automatedComment__message} markdown-body`}
                comment-testid={`Comment message html for automated comment ${commentId}`}
                /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                html={message as SafeHTMLString}
              />
            </div>
            {codePathsUrl && (
              <Button size="small" onClick={() => setIsCodePathsDialogOpen(true)} data-testid="show-paths-button">
                Show paths
              </Button>
            )}
            {automatedCommentId && (
              <CommentSnapshotProvider commentSnapshot={commentSnapshot}>
                <AutomatedReviewSuggestion
                  commentTitle={title}
                  commentAuthorLogin={comment.author?.login}
                  commentDatabaseId={comment.databaseId}
                  commentSource={source}
                  isDismissed={isDismissed}
                  commitMessageGenerationConfig={commitMessageGenerationConfig}
                  foldLongSuggestion={foldLongSuggestion}
                  feedbackOptions={feedbackOptions}
                  isAgenticAutofixEnabled={isAgenticAutofixEnabled}
                  isClassicExperience={isClassicExperience}
                  suggestion={suggestion}
                  suggestionState={suggestionState}
                  suggestionErrorMessage={suggestionErrorMessage}
                  automatedCommentId={automatedCommentId}
                  basePath={basePath}
                  askCopilotAction={askCopilotAction}
                  threadId={threadId}
                  fixWithCopilotProps={
                    // showFixWithCopilotButton is a server-side prop showing/hidding the button if supported for the type of automated comment.
                    // We disable fix with copilot for code scanning and code quality comments as requested in https://github.com/github/code-scanning/issues/23570#issuecomment-5176392984
                    // the `ccr_implement_suggestion_refresh_commit` feature flag is evaluated server-side, in the copilot evaluator https://github.com/github/github/pull/444690
                    showFixWithCopilotButton && isFixWithCopilotAvailable !== false
                      ? {
                          commentPermalink: comment.url,
                          modelsUrl,
                        }
                      : undefined
                  }
                  assignToCopilotProps={visibleAssignToCopilotProps}
                />
              </CommentSnapshotProvider>
            )}
            {showLegacyApplyChangesButton && (
              <div className="d-flex flex-items-center flex-wrap gap-2">
                {!suggestionHostsAskCopilot && askCopilotAction}
                <CopilotApplyChangesButton
                  commentPermalink={comment.url}
                  commentCount={1}
                  basePath={basePath}
                  modelsUrl={modelsUrl}
                  surface={surface}
                  suggestion={suggestion}
                  suggestionState={suggestionState}
                  commentMessage={message}
                />
              </div>
            )}
            {showFootBar && (
              <div className="d-flex flex-items-center flex-justify-between gap-2 mb-1">
                {showReactions && (
                  <ReactionViewerBase
                    ref={reactionButtonRef}
                    reactionGroups={reactionGroups ?? []}
                    onReact={onReact}
                    canReact={comment.viewerCanReact}
                  />
                )}
                {showFeedbackButtons && (
                  <AutomatedReviewFeedback
                    automatedCommentId={automatedCommentId}
                    basePath={basePath}
                    feedbackOptions={feedbackOptions}
                  />
                )}
                {showFootnoteText ? (
                  <div className="text-small color-fg-muted">
                    {footnoteTextHTML ? (
                      <UnsafeHTMLText html={footnoteTextHTML} />
                    ) : showAiNote ? (
                      'Copilot uses AI. Check for mistakes.'
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
      {codePathsUrl && isCodePathsDialogOpen && (
        <Dialog title={title || 'Code paths'} onClose={() => setIsCodePathsDialogOpen(false)} width="xlarge">
          <IncludeFragment src={codePathsUrl}>
            <div className="d-flex flex-justify-center tmp-p-4">
              <Spinner size="medium" />
            </div>
          </IncludeFragment>
        </Dialog>
      )}
    </div>
  )
}

export function AutomatedReviewThreadCommentErrorFallback() {
  return (
    <div className={`position-relative`}>
      <Blankslate border>
        <Blankslate.Visual>
          <AlertIcon size={24} className="fgColor-muted tmp-mt-3 tmp-mb-3" />
        </Blankslate.Visual>
        <Blankslate.Heading>
          <strong>There was a problem loading this review comment</strong>
        </Blankslate.Heading>
        <div className="mb-n2">
          <Blankslate.Description>Refresh the page or try again later</Blankslate.Description>
        </div>
        <Blankslate.SecondaryAction href="https://www.githubstatus.com/">GitHub status</Blankslate.SecondaryAction>
      </Blankslate>
    </div>
  )
}
